import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { clearForecastsQueryCache } from '@futrli/views/queries/forecasts';
import { getPredictForecastSalesUrl } from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';

import { BudgetCreatedWizardScreen } from './screens/budget-created-wizard-screen';
import { BudgetCreatingWizardScreen } from './screens/budget-creating-wizard-screen';
import { BudgetFormWizardScreen } from './screens/budget-form-wizard-screen';
import { ForecastType as ForecastTypeType } from '../../../dashboard/types';
import { trackCreateForecastEvent } from '../../../dashboard/utils';
import { useCreateForecastWizardContext } from '../../contexts/create-forecast-wizard-context';
import { useCreateOrUploadForecast } from '../../hooks/useCreateOrUploadForecast';
import { useOrgRouteParams } from '../../../../hooks/useOrgRouteParams/useOrgRouteParams';

export interface CreateBudgetWizardContainerProps {
  onClose: () => void;
}

export const CreateBudgetWizardContainer = ({ onClose }: CreateBudgetWizardContainerProps) => {
  const navigate = useNavigate();

  const { workspaceId, organisationId } = useOrgRouteParams();
  const { form, excelField, isExcelUpload, view } = useCreateForecastWizardContext();
  const { errorCreating, isCreating, isLoading, newForecastId: newStandardForecastId } = view.state;

  const { getExcelRequestData, getRequestData, validate } = form;
  const { file: excelFile } = excelField;

  const {
    createForecastFromExcelFile,
    error: excelError,
    isSaving: isSavingExcel,
    newForecastId: newExcelForecastId
  } = useCreateOrUploadForecast(workspaceId, organisationId);

  const error = errorCreating ? true : excelError;
  const newForecastId = newStandardForecastId || newExcelForecastId;

  const screen = (() => {
    if (isLoading) {
      return 'loading';
    }
    if (isCreating || isSavingExcel) {
      return 'creating';
    }
    if (newForecastId) {
      return 'created';
    }
    return 'form';
  })();

  const handleCreate = () => {
    if (validate()) {
      if (isExcelUpload && excelFile) {
        createForecastFromExcelFile(excelFile, getExcelRequestData('budget'));
      } else {
        view.createForecast(getRequestData());
      }
    }
  };

  const handleView = () => {
    if (newForecastId) {
      trackCreateForecastEvent(ForecastTypeType.BUDGET);
      onClose();
      navigate(getPredictForecastSalesUrl(workspaceId, organisationId, newForecastId));
    }
  };

  useEffect(() => {
    if (newExcelForecastId) {
      clearForecastsQueryCache(organisationId, workspaceId);
    }
  }, [newExcelForecastId, organisationId, workspaceId]);

  return (
    <>
      {screen === 'loading' && <LoadingWizardScreen />}
      {screen === 'form' && <BudgetFormWizardScreen error={error} onClose={onClose} onCreate={handleCreate} />}
      {screen === 'creating' && <BudgetCreatingWizardScreen />}
      {screen === 'created' && <BudgetCreatedWizardScreen onClose={onClose} onView={handleView} />}
    </>
  );
};
